export type ExperimentName = `experiment.${string}`
type Experiment = {
  defaultValue: 'noVariation'
  urlPatterns: Array<string>
}

/**
 * This dictionary holds the configuration for all the experiments currently
 * being assigned by the experiments worker.
 *
 * @property defaultValue - Must be set to noVariation for all of them.
 * @property urlPatterns - Allows you to scope each experiment to their
 * associated set of pages. Those patterns are used on the worker side
 * to know if the experiment will be used to process the current request
 * or not. It helps us improving our cache hit ratio, and thus the
 * overall server-side performance.
 */
export const config = {
  'experiment.globalAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.homeAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page */],
  },
  'experiment.landingAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* Landing page */],
  },
  'experiment.productAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.checkoutAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.homeCMSPageName': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page - permanent AB test */],
  },
  'experiment.goodDealsEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - GoodDeal */,
    ],
  },
  'experiment.recyclingEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Recycling */,
    ],
  },
  'experiment.holidaysEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Holidays */,
    ],
  },
  'experiment.homeBuybackPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages - permanent AB test */,
    ],
  },
  'experiment.smartphonesUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Smartphones */,
    ],
  },
  'experiment.laptopsUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Laptops */,
    ],
  },
  'experiment.tabletsUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Tablets */,
    ],
  },
  'experiment.audioUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Audio */,
    ],
  },
  'experiment.gamingUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Gaming */,
    ],
  },
  'experiment.appliancesUniversePageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* Universe pages - permanent AB test - Appliances */,
    ],
  },
  // https://backmarket.atlassian.net/wiki/spaces/SR/pages/5334073749/PRD+Gift+Cards+POC?focusedCommentId=5750030399
  'experiment.giftCards': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGiftCards
     * withoutGiftCards */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableScalapayIT': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (in the footer) */],
  },
  'experiment.reviewSellerAndFilters': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * showSeller */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/p/.*' /* Product Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/s/.*' /* Seller Reviews pages */,
    ],
  },
  'experiment.reviewAmount': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * tenReviews
     * sevenReviews */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.reviewsAiSummary': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAIReview
     * withAIReview
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
    ],
  },
  'experiment.reviewsNewAITags': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAITags
     * withAITags
     * withHighlightedAITags
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
    ],
  },
  'experiment.reviewsHelpRequestStatus': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutHelpRequestStatus
     * withHelpRequestStatus
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* All reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* Seller pages */,
    ],
  },
  'experiment.reviewsReviewerInformation': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
    /** Variations:
     * noVariation
     * withReviewerInformation
     * withoutReviewerInformation
     */
  },
  'experiment.sellerRatingPDP': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
    /** Variations:
     * noVariation
     * withSellerRating
     * withoutSellerRating
     */
  },
  'experiment.usModularPDP': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * enable
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.expressCheckout': {
    /** Variations:
     * enabled
     * disabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutCollectionPointOption': {
    /** Variations:
     * collectionPointEnabled
     * collectionPointDisabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutServiceExtraction': {
    /** Variations:
     * useBadoom
     * useCheckoutService
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.serviceFeeGrid': {
    /** Variations:
     * newServiceFeeGrid
     * currentServiceFeeGrid
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },

  'experiment.countrySwitchRedirection': {
    /** Variations:
     * withRedirection
     * withoutRedirection */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.deliveryDateAdjustment': {
    /** Variations:
     * withAdjustment
     * withoutAdjustment */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSales': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSalesOnPDP': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.newOfferPageV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGraph
     * withoutGraph */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.resaleBankStep': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBankStep
     * withoutBankStep */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.ppReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutReviewsInCarousel
     * withReviewsInCarousel */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.promoCodeInCart': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.hidePlpBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.isBatteryEducationEnabled': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.ppJapanBlackFridaySpecial': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBlackFridaySpecial
     * withoutBlackFridaySpecial */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanBatteryPickerUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBatteryPickerUpdated
     * withoutBatteryPickerUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppJapanPremiumDefinitionUpdated': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPremiumDefinitionUpdated
     * withoutPremiumDefinitionUpdated */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.tradeInAU': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/en-au/p/.*' /* Product page */],
  },
  'experiment.tradeInJP': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppUpgradeForLess': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUpgrade
     * withoutUpgrade */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.cmsPromotionalBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * controlText
     * variantText
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (above Header) */],
  },
  'experiment.skinnyOrPromotionalBannerV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * skinnyBanner
     * promotionalBanner
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}' /* Home page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/search' /* SearchResultPage */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* SellerPage */,
    ],
  },
  'experiment.ppTelcoCalculator': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCalculator
     * withouCalculator */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppAutomatedReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAutomatedReviews
     * withoutAutomatedReviews */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.enableEsUsContent': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableEsUsLocaleSelector': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.inputRedesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.ppAppleWatchBand': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBand
     * withoutBand */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */,
    ],
  },
  'experiment.ppUnitSold': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUnitSold
     * withoutUnitSold */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.plpDuplicatedAlgoliaCalls': {
    defaultValue: 'noVariation',
    /** Variations:
     * withDuplicatedCalls
     * withoutDuplicatedCalls */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */],
  },
  'experiment.buybackIconV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCircle
     * withPiggy
     * withHand */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.precisionGradingV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.ppPriceOpportunity': {
    defaultValue: 'noVariation',
    /** Variations:
     * withPriceOpportunity
     * withoutPriceOpportunity */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.postalCodePromise': {
    defaultValue: 'noVariation',
    /** Variations:
     * postalCodeEnabled
     * postalCodeDisabled
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.plpWideContainer': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withWideContainer
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.backMarketPromise': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBackMarketPromise
     * withoutBackMarketPromise */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.refurbProcess': {
    defaultValue: 'noVariation',
    /** Variations:
     * withRefurbProcess
     * withoutRefurbProcess */
    urlPatterns: ['^/(en|es)-us/p/.*' /* Product page */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/IRS/pages/6016303277/PRD+Annual+recurring+subscriptions
  'experiment.annualRecurring': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAnnualRecurring
     * withoutAnnualRecurring */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.usNewBattery': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBatteryPicker
     * withoutBatteryPicker */
    urlPatterns: ['^/[a-z]{2}-us/p/.*' /* Product page */],
  },
  'experiment.ppJapanHistoricalPrice': {
    defaultValue: 'noVariation',
    /** Variations:
     * withHistoricalPrice
     * withoutHistoricalPrice */
    urlPatterns: ['^/ja-jp/p/.*' /* JP Product page */],
  },
} satisfies Record<ExperimentName, Experiment>
